import { Component } from '@verndale/core';
import { debounce } from 'lodash';

class NavigationJumplinks extends Component {
  setupDefaults() {
    //dom setup
    this.setupDom();

    //observers setup
    this.menuWrapperSize = 0;
    this.menuSize = 0;
    this.menuInvisibleSize = 0;
    this.paddleMargin = 0;
    this.isJumping = false;
    this.jumpingId = '';
    this.largeContentVisibleThreshold = 0.2;
    this.thresholdVisible = 0.5;
    this.thresholdOverflow = 0.4;
    this.thresholdNotVisible = 0.25;
    this.lastScroll = 0;
    this.scrollDirection = '';

    this.observerOptions = {
      threshold: [
        this.largeContentVisibleThreshold,
        this.thresholdVisible,
        this.thresholdOverflow,
        this.thresholdNotVisible
      ]
    };

    this.generateJumpLinks();

    setTimeout(() => {
      this.initObserver();
    }, 300);
  }

  addListeners() {
    window.addEventListener('scroll', this.handleVerticalScroll.bind(this));
    window.addEventListener(
      'resize',
      debounce(this.handleResize.bind(this), 250)
    );
    this.dom.dropdown?.addEventListener(
      'change',
      this.handleDropdownChange.bind(this)
    );
  }

  setupDom() {
    this.dom = {
      header: document.querySelector('.header'),
      // paddles
      leftPaddle: this.el.querySelector('.navigation-jumplinks__paddle--left'),
      rightPaddle: this.el.querySelector(
        '.navigation-jumplinks__paddle--right'
      ),
      // sections
      sectionsData: document.querySelectorAll(
        '[data-section]:not([data-section] [data-section])'
      ),
      // items
      items: this.el.querySelectorAll('.navigation-jumplinks__item'),
      links: this.el.querySelectorAll('.navigation-jumplinks__link'),
      // wrapper
      wrapper: this.el.querySelector('.navigation-jumplinks__wrapper'),
      //jumplinks
      jumplinks: this.el.querySelector('.navigation-jumplinks__list'),
      dropdown: this.el.querySelector('.field__select')
    };

    this.initWidths();
  }

  initObserver() {
    const observer = new IntersectionObserver(
      this.observerCallback.bind(this),
      this.observerOptions
    );
    this.dom.sectionsData?.forEach(el => observer.observe(el));
  }

  observerCallback(entries) {
    entries.forEach(entry => {
      if (entry.intersectionRatio >= this.thresholdVisible && !this.isJumping) {
        this.handleActive(entry);
        return;
      }

      if (
        entry.intersectionRatio >= this.thresholdOverflow &&
        !this.isJumping &&
        this.isBiggerElement(entry.target) &&
        this.scrollDirection === 'down' &&
        !this.isElementOverflowingTop(entry.target)
      ) {
        this.handleActive(entry);
        return;
      }

      const moduleHeight = entry.target.getBoundingClientRect().height;

      if (
        entry.intersectionRatio < this.thresholdNotVisible &&
        this.scrollDirection === 'up' &&
        !this.isElementOverflowingTop(entry.target) &&
        this.isBiggerElement(entry.target) &&
        moduleHeight > 2000
      ) {
        this.handleInactive(entry);
      }

      if (
        entry.intersectionRatio < this.thresholdNotVisible &&
        !this.isJumping &&
        this.scrollDirection === 'up' &&
        moduleHeight <= 2000
      ) {
        this.handleInactive(entry);
        return;
      }

      if (
        entry.intersectionRatio >= this.largeContentVisibleThreshold &&
        moduleHeight > 2000 &&
        this.scrollDirection === 'down' &&
        !this.isJumping
      ) {
        let shouldReturn = false;
        const id = entry.target.id;
        this.dom.links.forEach((el, index) => {
          if (el.dataset.id === id) {
            const next = this.dom.links[index + 1];
            if (next.classList.contains('active')) {
              shouldReturn = true;
              return;
            }
          }
        });
        if (shouldReturn) return;
        this.handleActive(entry);
        return;
      }

      if (this.isJumping && entry.target.id === this.jumpingId) {
        setTimeout(() => {
          this.isJumping = false;
        }, 500);
      }
    });
  }

  isBiggerElement(el) {
    const elementHeight = el.getBoundingClientRect().height;
    const viewportHeight = window.innerHeight;

    return elementHeight > viewportHeight - 250;
  }

  isElementOverflowingTop(el) {
    const elementTop = el.getBoundingClientRect().top;
    return elementTop < 150;
  }

  generateJumpLinks() {
    const wrapper = this.dom.wrapper;
    const dropdown = this.dom.dropdown;
    const paddles = wrapper?.querySelector('.navigation-jumplinks__paddles');

    wrapper.innerHTML = '';

    let links = '';

    this.dom.sectionsData?.forEach((section, index) => {
      const link = `
      <li class="navigation-jumplinks__item">
        <a href="#${section.id}" 
          aria-label="${section.dataset.section}"
          data-id="${section.id}" class="navigation-jumplinks__link" ${
        section.dataset.dataContractorLayer
          ? `data-layer="[${section.dataset.dataContractorLayer}]"`
          : ''
      }>
        ${section.dataset.section}
        </a>
      </li>
      `;
      if (section.dataset.section) {
        links += link;
      }

      const option = document.createElement('option');
      option.value = section.id;
      option.text = this.decodeHtmlEntities(section.dataset.section);
      if (index === 0) {
        option.selected = true;
      }
      if (section.dataset.section) {
        dropdown.appendChild(option);
      }
    });

    wrapper.innerHTML = `
    <ol class="navigation-jumplinks__list" role="list">
    ${links}
    </ol>
    ${paddles.outerHTML}
    `;

    this.setupDom();

    this.dom.jumplinks?.addEventListener(
      'scroll',
      this.handleHorizontalScroll.bind(this),
      { passive: true }
    );

    this.dom.rightPaddle?.addEventListener(
      'click',
      this.handleRightPaddleClick.bind(this)
    );

    this.dom.leftPaddle?.addEventListener(
      'click',
      this.handleLeftPaddleClick.bind(this)
    );

    this.dom.links?.forEach(link => {
      link.addEventListener('click', this.handleJumpToSection.bind(this));
    });

    this.handleResize();
  }

  initWidths() {
    //modify paddles viewport
    const rightPaddleSvg = this.dom.rightPaddle.querySelector('svg');
    const leftPaddleSvg = this.dom.leftPaddle.querySelector('svg');
    rightPaddleSvg.setAttribute('viewBox', '0 0 120 120');
    leftPaddleSvg.setAttribute('viewBox', '0 0 120 120');

    // get wrapper width
    this.menuWrapperSize = this.getWrapperWidth();
    // get menu size
    this.menuSize = this.getItemsSize();
    // get how much of menu is invisible
    this.menuInvisibleSize = this.menuSize - this.menuWrapperSize;
    // get some relevant size for the paddle triggering point
    this.paddleMargin = 16;
  }

  getWrapperWidth() {
    return this.dom.wrapper?.offsetWidth;
  }

  getItemsSize() {
    let itemSize = -16; // don't count the gap for the first item
    const allItems = this.el.querySelectorAll('.navigation-jumplinks__item');

    allItems?.forEach(item => {
      itemSize += item.offsetWidth + 32; // 20 = gap size
    });

    return itemSize;
  }

  decodeHtmlEntities(encodedString) {
    const elem = document.createElement('div');
    elem.innerHTML = encodedString;
    return elem.textContent;
  }

  generateJumpLinks() {
    const wrapper = this.dom.wrapper;
    const dropdown = this.dom.dropdown;
    const paddles = wrapper?.querySelector('.navigation-jumplinks__paddles');

    wrapper.innerHTML = '';

    let links = '';

    this.dom.sectionsData?.forEach((section, index) => {
      const link = `
      <li class="navigation-jumplinks__item">
        <a href="#${section.id}" 
          aria-label="${section.dataset.section}"
          data-id="${section.id}" class="navigation-jumplinks__link" ${
        section.dataset.dataContractorLayer
          ? `data-layer="[${section.dataset.dataContractorLayer}]"`
          : ''
      }>
        ${section.dataset.section}
        </a>
      </li>
      `;
      if (section.dataset.section) {
        links += link;
      }

      const option = document.createElement('option');
      option.value = section.id;
      option.text = this.decodeHtmlEntities(section.dataset.section);
      if (index === 0) {
        option.selected = true;
      }
      if (section.dataset.section) {
        dropdown.appendChild(option);
      }
    });

    wrapper.innerHTML = `
    <ol class="navigation-jumplinks__list" role="list">
    ${links}
    </ol>
    ${paddles.outerHTML}
    `;

    this.setupDom();

    this.dom.jumplinks?.addEventListener(
      'scroll',
      this.handleHorizontalScroll.bind(this),
      { passive: true }
    );

    this.dom.rightPaddle?.addEventListener(
      'click',
      this.handleRightPaddleClick.bind(this)
    );

    this.dom.leftPaddle?.addEventListener(
      'click',
      this.handleLeftPaddleClick.bind(this)
    );

    this.dom.links?.forEach(link => {
      link.addEventListener('click', this.handleJumpToSection.bind(this));
    });

    this.handleResize();
  }

  getMenuPosition() {
    return this.dom.jumplinks?.scrollLeft;
  }

  getScrollingDirection() {
    const currentScroll = window.pageYOffset;
    let direction = '';

    if (currentScroll > this.lastScroll) {
      direction = 'down';
    } else if (currentScroll < this.lastScroll) {
      direction = 'up';
    }

    this.lastScroll = currentScroll;
    return direction ? direction : this.scrollDirection;
  }

  detectSticky() {
    const headerHeight = this.dom.header?.clientHeight;
    const stickyElementTop = this.el.getBoundingClientRect().top;

    if (headerHeight && stickyElementTop <= headerHeight + 5) {
      this.el.classList.add('sticky');
    } else {
      this.el.classList.remove('sticky');
    }

    if (this.el.classList.contains('sticky')) {
      document.documentElement.style.setProperty(
        '--jumplinks-block-size',
        `${this.el.offsetHeight}px`
      );
    } else {
      document.documentElement.style.setProperty(
        '--jumplinks-block-size',
        '0px'
      );
    }
  }

  initPaddles() {
    this.menuInvisibleSize = this.menuSize - this.menuWrapperSize;
    const rightPaddle = this.dom.rightPaddle;
    const leftPaddle = this.dom.leftPaddle;

    if (this.menuInvisibleSize <= 0) {
      rightPaddle?.classList.add('hidden');
      leftPaddle?.classList.add('hidden');
    } else {
      rightPaddle?.classList.remove('hidden');
    }
  }

  handleVerticalScroll() {
    this.scrollDirection = this.getScrollingDirection();
    this.detectSticky();
  }

  handleInactive(entry) {
    const currentSection = entry.target.id;
    let previousItem;

    this.dom.links?.forEach(el => {
      if (el.dataset.id === currentSection && el.classList.contains('active')) {
        if (previousItem) {
          el.classList.remove('active');
          previousItem.classList.add('active');
          this.dom.dropdown.value = previousItem.dataset.id;
        }
      }

      previousItem = el;
    });
  }

  handleActive(entry) {
    const currentSection = entry.target.id;

    this.dom.links?.forEach(el => {
      el.classList.remove('active');

      if (el.dataset.id === currentSection) {
        this.dom.dropdown.value = currentSection;
        el.classList.add('active');
        //get element horizontal scroll distance from container
        const elementOffset = el.offsetLeft;
        //get container horizontal scroll distance
        const containerOffset = this.dom.jumplinks?.scrollLeft;
        //scroll to element
        setTimeout(() => {
          this.dom.jumplinks.scrollLeft = elementOffset - containerOffset - 32; //32 = offset
        }, 500);
      }
    });
  }

  handleResize() {
    this.menuWrapperSize = this.getWrapperWidth();
    this.menuSize = this.getItemsSize();
    this.menuInvisibleSize = this.menuSize - this.menuWrapperSize;
    this.initPaddles();
  }

  handleHorizontalScroll() {
    // get how much of the menu is invisible
    this.menuInvisibleSize = this.menuSize - this.menuWrapperSize;
    // get how much we have scrolled so far
    const menuPosition = this.getMenuPosition();
    //return (this.dom.jumplinks as HTMLElement)?.scrollLeft;
    const menuEndOffset = this.menuInvisibleSize - this.paddleMargin;

    const leftPaddle = this.dom.leftPaddle;
    const rightPaddle = this.dom.rightPaddle;

    // show and hide the paddles depending on menu position
    if (menuPosition <= this.paddleMargin) {
      leftPaddle?.classList.add('hidden');
      rightPaddle?.classList.remove('hidden');
    } else if (menuPosition - this.paddleMargin < menuEndOffset) {
      // show both paddles in the middle
      leftPaddle.classList.remove('hidden');
      rightPaddle.classList.remove('hidden');
    } else if (menuPosition >= menuEndOffset) {
      leftPaddle.classList.remove('hidden');
      rightPaddle.classList.add('hidden');
    }
  }

  handleRightPaddleClick() {
    this.dom.jumplinks.scrollLeft += 200;
  }

  handleLeftPaddleClick() {
    this.dom.jumplinks.scrollLeft -= 200;
  }

  handleJumpToSection(e) {
    e.preventDefault();
    const currentTarget = e.currentTarget;

    this.isJumping = true;
    this.dom.links?.forEach(el => el.classList.remove('active'));

    currentTarget.classList.add('active');
    currentTarget.blur();

    this.jumpingId = currentTarget.dataset.id;

    const element = Array.from(this.dom.sectionsData).find(
      el => el.id === this.jumpingId
    );

    //get scroll distance from top
    const elementOffset = element?.offsetTop;

    //scroll to element
    window.scrollTo({
      top: elementOffset - 200,
      behavior: 'smooth'
    });

    // add hash to url or replace it
    if (window.location.hash) {
      window.history.replaceState(null, '', `#${currentTarget.dataset.id}`);
    } else {
      window.history.pushState(null, '', `#${currentTarget.dataset.id}`);
    }
  }

  handleDropdownChange(e) {
    const target = e.target;
    const selected = target.value;
    this.isJumping = true;
    this.jumpingId = selected;

    const element = Array.from(this.dom.sectionsData).find(
      el => el.id === selected
    );
    element?.scrollIntoView({ behavior: 'smooth' });
  }
}

export default NavigationJumplinks;
